import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDateTime } from "@/lib/dates";
import { toast } from "sonner";

// iter-69c — Auto-classroom-transitions panel moved out of the Compliance
// page (per user request) into its own file so it can render under the
// Licensing File area without breaking Compliance's render tree.

function AutoTransitionsPanel() {
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [cfg, lg] = await Promise.all([
        api.get("/transitions/config").then((r) => r.data),
        api.get("/transitions/log?limit=20").then((r) => r.data),
      ]);
      setConfig(cfg);
      setLog(lg);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/transitions/config", { ...config, ...patch });
      setConfig(r.data);
      toast.success("Saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const runNow = async (dry) => {
    setRunning(true);
    try {
      const r = await api.post(`/transitions/run-now?dry_run=${dry}`);
      const d = r.data;
      toast.success(`${d.moved} moved · ${d.needs_review} need review · ${d.transitions.length} children scanned`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  // Authenticated PDF download — the JWT lives in localStorage and is only
  // attached by the axios `api` client. A plain <a href> here bypassed axios
  // and the backend returned 401 'Not authenticated'. Fetch as a blob and
  // trigger the download manually so the Authorization header is sent.
  const downloadTransitionPdf = async (t) => {
    try {
      const res = await api.get(`/children/${t.child_id}/forms/id/${t.form_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (t.child_name || "child").replace(/\s+/g, "_");
      a.href = url;
      a.download = `DCY01229-${safeName}-${(t.created_at || "").slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!config) return null;

  return (
    <section data-testid="auto-transitions-panel" className="rounded-3xl border border-stone-200 bg-white p-8 space-y-5">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Auto Transitions</div>
          <h2 className="mt-2 font-display text-2xl text-stone-900">Classroom transitions (DCY 01229)</h2>
          <p className="mt-1 text-sm text-stone-500">Move children up a classroom when they age into the next bucket. A populated DCY 01229 Transition Letter is auto-filed in each child&apos;s Compliance area.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" data-testid="transitions-run-dry" onClick={() => runNow(true)} disabled={running} variant="outline" className="rounded-xl">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Preview
          </Button>
          <Button type="button" data-testid="transitions-run-now" onClick={() => runNow(false)} disabled={running} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Run now
          </Button>
        </div>
      </header>

      <div className="grid sm:grid-cols-4 gap-3">
        <div className="rounded-xl border border-stone-200 p-3">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Enabled</Label>
          <div className="mt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="transitions-enabled"
                checked={!!config.enabled}
                onChange={(e) => save({ enabled: e.target.checked })}
                disabled={busy}
                className="rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
              />
              {config.enabled ? "Active" : "Off"}
            </label>
          </div>
        </div>
        <div className="rounded-xl border border-stone-200 p-3">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Cadence</Label>
          <select
            data-testid="transitions-cadence"
            value={config.cadence || "daily"}
            onChange={(e) => save({ cadence: e.target.value })}
            disabled={busy}
            className="mt-2 w-full h-9 rounded-md border border-stone-200 px-2 text-sm bg-white"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly (Mondays)</option>
          </select>
        </div>
        <div className="rounded-xl border border-stone-200 p-3 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Notify admins (comma-separated)</Label>
          <Input
            data-testid="transitions-notify-emails"
            defaultValue={(config.notify_emails || []).join(", ")}
            onBlur={(e) => save({ notify_emails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            disabled={busy}
            placeholder="director@center.com, admin@center.com"
            className="mt-2 h-9"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {[
          ["infant_to_toddler", "Infant → Toddler"],
          ["toddler_to_preschool", "Toddler → Preschool"],
          ["preschool_to_school_age", "Preschool → School Age"],
        ].map(([k, label]) => (
          <div key={k} className="rounded-xl border border-stone-200 p-3">
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                data-testid={`transitions-threshold-${k}`}
                type="number"
                defaultValue={config.thresholds_months?.[k] ?? ""}
                onBlur={(e) => save({ thresholds_months: { ...config.thresholds_months, [k]: parseInt(e.target.value || 0) } })}
                disabled={busy}
                className="h-9"
              />
              <span className="text-xs text-stone-500">months</span>
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">Recent transitions ({log.length})</div>
        {log.length === 0 ? (
          <p className="text-sm text-stone-500">No transitions yet. Click <b>Run now</b> to fire the scan against today&apos;s rosters.</p>
        ) : (
          <div className="rounded-xl border border-stone-200 overflow-hidden" data-testid="transitions-log">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Child</th>
                  <th className="px-3 py-2 text-left">Move</th>
                  <th className="px-3 py-2 text-left">Age</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">DCY 01229</th>
                </tr>
              </thead>
              <tbody>
                {log.map((t) => (
                  <tr key={t.id} className="border-t border-stone-100" data-testid={`transition-row-${t.id}`}>
                    <td className="px-3 py-2 text-xs text-stone-600">{fmtDateTime(t.created_at)}</td>
                    <td className="px-3 py-2 font-medium text-stone-900">{t.child_name}</td>
                    <td className="px-3 py-2 text-stone-700">{t.from_label} → <b>{t.to_label}</b></td>
                    <td className="px-3 py-2 text-stone-600">{t.age_months}m</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                        t.status === "moved" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                        t.status === "needs_review" ? "bg-red-50 text-red-800 border border-red-200" :
                        "bg-stone-100 text-stone-600 border border-stone-200"
                      }`}>{t.status.replace("_", " ")}</span>
                      {t.missing_fields && t.missing_fields.length > 0 && (
                        <span className="ml-2 text-[10px] text-rose-700">missing: {t.missing_fields.join(", ")}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {t.form_id ? (
                        <Button
                          type="button"
                          data-testid={`transition-pdf-${t.id}`}
                          onClick={() => downloadTransitionPdf(t)}
                          variant="outline"
                          className="rounded-md h-7 text-xs px-2.5"
                        >
                          Download
                        </Button>
                      ) : (
                        <span className="text-[10px] text-stone-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}


export default AutoTransitionsPanel;
